/**
 * Layout quadriculado — estrelas viram blocos, cauda vira linha.
 *
 * A ideia veio do jeito que o engenheiro desenha à mão. Medindo os 201 sysmaps
 * baixados do Zabbix, dois padrões aparecem toda hora:
 *
 *   - equipamento com vários pendurados nele (OLT, switch de POP, concentrador)
 *     tem esses pendurados arrumados num BLOCO retangular, não espalhados em
 *     leque. Achei 52 estrelas com 4+ folhas nos 5 mapas maiores; a maior tem 9.
 *   - coisa homogênea e repetida — os AS de BGP são o caso clássico — vai em
 *     LINHA. O mapa da VMAX tem uma estrela de 6 folhas desenhada em 6 colunas
 *     por 1 linha, que é literalmente isso.
 *
 * O passo mais comum entre folhas nesses mapas é 160px. Não dá pra usar esse
 * número cru: lá o ícone é pequeno e o rótulo curto; aqui o nó tem 72px e o
 * rótulo vai a 200px. Então o passo é derivado da pegada real, e o 160 serve só
 * como confirmação de que a PROPORÇÃO (bloco um pouco mais largo que alto)
 * bate com o que o engenheiro faz.
 *
 * O núcleo (o que sobra depois de descascar as folhas) continua sendo
 * posicionado pelo layout radial, que já resolve cruzamento e sobreposição.
 * Este modo só muda o que fazer com as PONTAS.
 */

import radialLayout, {
  resolverColisoes,
  afastarDeArestas,
  PEGADA_X,
  PEGADA_Y,
  PISO,
  RAIO_NO,
} from './radialLayout';

// Passo do bloco. X respeita a largura do rótulo, Y respeita o piso de
// distância mínima — assim a garantia do radial continua valendo aqui.
const PASSO_X = Math.max(PEGADA_X + 20, PISO);
const PASSO_Y = Math.max(PEGADA_Y + 40, PISO);

// Mínimo de folhas pra virar bloco. Com 2 o "bloco" é um par, que não ganha
// nada em ser tratado à parte e ainda tira o nó do lugar que o radial deu.
const MIN_ESTRELA = 3;

// Folga entre corpos vizinhos. Varrido em 90/140/190: 140 dá o menor número
// de cruzamentos na HZ (2594) e a menor área, e ainda derruba aresta-sobre-nó.
// 190 espalha demais e os cruzamentos voltam a subir.
const FOLGA = 140;

// Snap final. Mantém a cara quadriculada sem comer a distância mínima: 20px de
// snap desloca no máximo ~14px na diagonal.
const SNAP = 20;

/** Folha "linear": entidade repetida e homogênea, vai em fila e não em bloco. */
function ehLinear(id) {
  const s = String(id);
  return s.startsWith('ext-') || s.startsWith('seg-');
}

/**
 * Arruma k itens num retângulo perto do quadrado, um pouco mais largo que alto
 * — que é a proporção que os mapas desenhados à mão mostram.
 */
function bloco(ids, passoX, passoY) {
  const k = ids.length;
  const cols = Math.max(1, Math.ceil(Math.sqrt(k * 1.3)));
  const out = [];
  ids.forEach((id, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    out.push({ id, dx: c * passoX, dy: r * passoY });
  });
  const larg = (Math.min(k, cols) - 1) * passoX;
  // centraliza horizontalmente em torno de 0
  for (const o of out) o.dx -= larg / 2;
  return out;
}

/** Fila horizontal. Quebra em várias linhas se ficar larga demais. */
function linha(ids, passoX, passoY, maxCols = 8) {
  const cols = Math.min(Math.max(1, ids.length), maxCols);
  const out = [];
  ids.forEach((id, i) => {
    out.push({ id, dx: (i % cols) * passoX, dy: Math.floor(i / cols) * passoY });
  });
  const larg = (cols - 1) * passoX;
  for (const o of out) o.dx -= larg / 2;
  return out;
}

/**
 * Afasta corpos rígidos. Diferente do resolverColisoes do radial, aqui o que se
 * move é o GRUPO inteiro — se o bloco de 6 folhas fosse resolvido nó a nó ele
 * deixaria de ser quadrado, que é justamente a coisa que este modo existe pra
 * produzir.
 */
function resolverCorpos(corpos, iters = 250, folga = FOLGA) {
  if (corpos.length < 2) return corpos;
  const cell = Math.max(
    ...corpos.map((c) => Math.max(c.w, c.h)),
    PASSO_X,
  ) + folga;
  for (let it = 0; it < iters; it++) {
    const grade = new Map();
    corpos.forEach((c, i) => {
      const k = `${Math.floor(c.x / cell)},${Math.floor(c.y / cell)}`;
      if (!grade.has(k)) grade.set(k, []);
      grade.get(k).push(i);
    });
    let moveu = false;
    for (let i = 0; i < corpos.length; i++) {
      const a = corpos[i];
      const gx = Math.floor(a.x / cell);
      const gy = Math.floor(a.y / cell);
      for (let ddx = -1; ddx <= 1; ddx++) {
        for (let ddy = -1; ddy <= 1; ddy++) {
          for (const j of grade.get(`${gx + ddx},${gy + ddy}`) || []) {
            if (j <= i) continue;
            const b = corpos[j];
            let dx = a.x - b.x;
            let dy = a.y - b.y;
            if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) {
              dx = (Math.random() - 0.5) || 0.1;
              dy = (Math.random() - 0.5) || 0.1;
            }
            const ox = (a.w + b.w) / 2 + folga - Math.abs(dx);
            const oy = (a.h + b.h) / 2 + folga - Math.abs(dy);
            if (ox <= 0 || oy <= 0) continue;
            // empurra pelo eixo de menor penetração
            if (ox < oy) {
              const s = ((dx >= 0 ? 1 : -1) * ox) / 2;
              a.x += s; b.x -= s;
            } else {
              const s = ((dy >= 0 ? 1 : -1) * oy) / 2;
              a.y += s; b.y -= s;
            }
            moveu = true;
          }
        }
      }
    }
    if (!moveu) break;
  }
  return corpos;
}

/**
 * Posiciona todos os nós. Mesma assinatura do radialLayout.
 * Devolve Map(id -> {x, y}).
 */
export default function gridLayout(nodes, edges, explosao = 40) {
  const folgaEsc = 0.7 + explosao / 150;
  const px = PASSO_X * folgaEsc;
  const py = PASSO_Y * folgaEsc;

  const todos = new Set(nodes.map((n) => n.data.id));
  const adj = new Map();
  const liga = (a, b) => {
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a).add(b);
  };
  for (const e of edges) {
    const a = String(e.data.source);
    const b = String(e.data.target);
    if (!todos.has(a) || !todos.has(b) || a === b) continue;
    liga(a, b); liga(b, a);
  }
  const grau = (id) => (adj.get(id) ? adj.get(id).size : 0);

  // 1) descasca as folhas: nó de grau 1 pendurado num hub de grau >= 2
  const folhasDe = new Map();
  for (const id of todos) {
    const vs = adj.get(id);
    if (!vs || vs.size !== 1) continue;
    const hub = [...vs][0];
    if (grau(hub) < 2) continue;      // par solto não é estrela
    if (!folhasDe.has(hub)) folhasDe.set(hub, []);
    folhasDe.get(hub).push(id);
  }
  // estrela pequena demais volta pro núcleo
  for (const [hub, fs] of [...folhasDe]) {
    if (fs.length < MIN_ESTRELA) folhasDe.delete(hub);
  }
  const emEstrela = new Set();
  for (const fs of folhasDe.values()) for (const f of fs) emEstrela.add(f);

  // 2) núcleo posicionado pelo radial — ele já cuida de cruzamento,
  //    sobreposição e do bloco de isolados
  const core = [...todos].filter((id) => !emEstrela.has(id));
  const coreSet = new Set(core);
  const pos = radialLayout(
    core.map((id) => ({ data: { id } })),
    edges.filter((e) => coreSet.has(String(e.data.source)) && coreSet.has(String(e.data.target))),
    explosao,
  );

  // 3) cada estrela vira um corpo rígido: o hub e, logo abaixo, o bloco das
  //    folhas normais; depois dele, em fila, as folhas lineares (AS externo,
  //    segmento) — que é a ordem que você vê nos mapas desenhados à mão.
  const corpos = [];
  const soltos = [];
  for (const id of core) {
    const p = pos.get(id);
    if (!p) continue;
    const fs = folhasDe.get(id);
    if (!fs) {
      soltos.push({ ids: [id], off: [{ id, dx: 0, dy: 0 }], x: p.x, y: p.y, w: PEGADA_X, h: PEGADA_Y });
      continue;
    }
    const normais = fs.filter((f) => !ehLinear(f)).sort();
    const lineares = fs.filter(ehLinear).sort();

    const off = [{ id, dx: 0, dy: 0 }];
    let y = py;
    if (normais.length) {
      for (const o of bloco(normais, px, py)) off.push({ ...o, dy: o.dy + y });
      y += Math.ceil(normais.length / Math.max(1, Math.ceil(Math.sqrt(normais.length * 1.3)))) * py;
    }
    if (lineares.length) {
      for (const o of linha(lineares, px, py)) off.push({ ...o, dy: o.dy + y });
    }
    const dxs = off.map((o) => o.dx);
    const dys = off.map((o) => o.dy);
    corpos.push({
      ids: off.map((o) => o.id),
      off,
      x: p.x,
      y: p.y,
      w: (Math.max(...dxs) - Math.min(...dxs)) + PEGADA_X,
      h: (Math.max(...dys) - Math.min(...dys)) + PEGADA_Y,
    });
  }

  // 4) separa os corpos sem desmanchar nenhum bloco
  const todosCorpos = corpos.concat(soltos);
  resolverCorpos(todosCorpos);

  // 5) Acabamento por projeções alternadas.
  //
  //    O radial entrega o núcleo já com as arestas desviadas dos nós, mas
  //    mover os corpos no passo 4 desfaz isso -- medido: 136 arestas do núcleo
  //    passando por cima de nó do núcleo, contra 6 dentro dos blocos. Ou seja,
  //    o bloco nunca foi o problema; o problema era eu reposicionar depois.
  //
  //    Então as duas restrições são aplicadas alternadamente, em passos
  //    pequenos: desvia aresta e separa nó no espaço do núcleo, devolve pros
  //    corpos, separa corpo, e volta. Cada uma cede um pouco por vez em vez de
  //    uma sobrescrever a outra.
  const arestasCore = [];
  for (const e of edges) {
    const a = String(e.data.source);
    const b = String(e.data.target);
    if (coreSet.has(a) && coreSet.has(b) && a !== b) arestasCore.push([a, b]);
  }
  const ancora = new Map();          // id do hub/nó solto -> corpo
  for (const c of todosCorpos) ancora.set(c.off[0].id, c);
  const posCore = new Map();
  for (const [id, c] of ancora) posCore.set(id, { x: c.x, y: c.y });

  for (let k = 0; k < 10; k++) {
    afastarDeArestas(posCore, arestasCore, RAIO_NO + 24, 1);
    resolverColisoes(posCore, 20);
    for (const [id, c] of ancora) { const p = posCore.get(id); c.x = p.x; c.y = p.y; }
    resolverCorpos(todosCorpos, 20);
    for (const [id, c] of ancora) { const p = posCore.get(id); p.x = c.x; p.y = c.y; }
  }
  // última palavra é do corpo: bloco fora do lugar é o defeito mais visível
  resolverCorpos(todosCorpos, 200);

  const saida = new Map();
  for (const c of todosCorpos) {
    for (const o of c.off) saida.set(o.id, { x: c.x + o.dx, y: c.y + o.dy });
  }

  // 6) piso entre nós de corpos diferentes que a resolução por caixa não pega
  //    (a caixa é o bounding box, e o conteúdo não a preenche toda), depois
  //    encaixa na grade.
  resolverColisoes(saida, 120);
  for (const p of saida.values()) {
    p.x = Math.round(p.x / SNAP) * SNAP;
    p.y = Math.round(p.y / SNAP) * SNAP;
  }
  return saida;
}
